import { useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  InfiniteData,
  QueryClient,
  UseInfiniteQueryResult,
  UseMutationResult,
  UseQueryResult,
} from '@tanstack/react-query';
import { useTranslation } from '../i18n/useTranslation';
import { invalidateAiUsage } from './useAiUsage';
import {
  listComparisons,
  getComparison,
  createComparison,
  deleteComparison,
  deleteAllComparisons,
  fetchComparisonImages,
  fetchComparisonGalleryPage,
  type ComparisonDetail,
  type ComparisonGalleryPage,
  type ComparisonImageInfo,
  type CreateComparisonPayload,
} from '../services/aquariumComparisonService';
import type {
  ComparisonHistoryItem,
  ComparisonImage,
  ComparisonPhotoOption,
  ComparisonResult,
} from '../components/organisms/ComparisonView';

/**
 * Query-key factory scoped per aquariumId, following the pattern used by
 * useAquariums.ts / useTimeline.ts: a stable base key plus suffixed keys for
 * each query variant so invalidation can target exactly what changed.
 */
export const comparisonQueryKeys = {
  all: (aquariumId: string) => ['aquarium-comparisons', aquariumId] as const,
  history: (aquariumId: string) => ['aquarium-comparisons', aquariumId, 'history'] as const,
  detail: (aquariumId: string, comparisonId: string) =>
    ['aquarium-comparisons', aquariumId, 'detail', comparisonId] as const,
  images: (aquariumId: string, imageIds: readonly string[]) =>
    ['aquarium-comparisons', aquariumId, 'images', [...imageIds].sort().join(',')] as const,
  gallery: (aquariumId: string) => ['aquarium-comparisons', aquariumId, 'gallery'] as const,
};

/** Gallery page size of the "choose photos" picker. */
export const COMPARISON_GALLERY_PAGE_SIZE = 24;

const STALE_TIME_MS = 30_000;

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * A photo whose date differs from the requested target date by more than this
 * is flagged as the "nearest available photo" (the backend matches within ±1 day).
 */
export const NEAREST_MATCH_THRESHOLD_MS = 12 * 60 * 60 * 1000;

type ImageLookup = Record<string, ComparisonImageInfo>;

interface WithImages<T> {
  record: T;
  images: ImageLookup;
}

/** One page of the comparison history, with the images its records reference. */
interface HistoryPage extends WithImages<ComparisonDetail[]> {
  nextCursor: string | null;
}

/** The dates (epoch ms) a new comparison asked for: earlier photo first. */
export interface ComparisonTargetDates {
  before: number;
  after: number;
}

/**
 * Epoch ms of a requested custom date. A plain calendar date (yyyy-mm-dd, as the
 * date inputs produce) maps to local midday, so any photo taken on that local day
 * lies within NEAREST_MATCH_THRESHOLD_MS; a full ISO timestamp is used as-is.
 */
function parseTargetDate(value: string): number {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (match) {
    return new Date(Number(match[1]), Number(match[2]) - 1, Number(match[3]), 12).getTime();
  }
  return Date.parse(value);
}

/**
 * Target dates of a create-comparison request: a preset compares today with
 * today − N days; a custom range compares dateFrom with dateTo. Explicit image
 * selections have no target date (null).
 */
export function getComparisonTargetDates(
  payload: CreateComparisonPayload,
  now: number = Date.now(),
): ComparisonTargetDates | null {
  if ('dateOffsetDays' in payload) {
    return { before: now - payload.dateOffsetDays * DAY_MS, after: now };
  }
  if ('dateFrom' in payload) {
    const before = parseTargetDate(payload.dateFrom);
    const after = parseTargetDate(payload.dateTo);
    return Number.isNaN(before) || Number.isNaN(after) ? null : { before, after };
  }
  return null;
}

/** True when the photo's date is more than 12 hours away from the requested date. */
export function isNearestMatch(photoIso: string, targetMs: number): boolean {
  const photoMs = Date.parse(photoIso);
  if (Number.isNaN(photoMs)) return false;
  return Math.abs(photoMs - targetMs) > NEAREST_MATCH_THRESHOLD_MS;
}

/** Shared, cached image lookup so history/detail/create reuse each other's results. */
function loadImages(
  queryClient: QueryClient,
  aquariumId: string,
  imageIds: string[],
): Promise<ImageLookup> {
  if (imageIds.length === 0) return Promise.resolve({});
  return queryClient.fetchQuery({
    queryKey: comparisonQueryKeys.images(aquariumId, imageIds),
    queryFn: () => fetchComparisonImages(aquariumId, imageIds),
    staleTime: STALE_TIME_MS,
  });
}

/** Builds locale-aware formatters for mapping API records onto ComparisonView props. */
function useComparisonFormatters() {
  const { t, i18n } = useTranslation();
  const language = i18n.language;

  const formatDate = useCallback(
    (iso: string) => {
      const date = new Date(iso);
      if (Number.isNaN(date.getTime())) return iso;
      return new Intl.DateTimeFormat(language, { dateStyle: 'long' }).format(date);
    },
    [language],
  );

  /**
   * Maps one referenced image onto ComparisonView's image props. The date is the
   * photo's taken/captured date (see fetchComparisonImages), falling back to the
   * comparison's creation date when the image could not be resolved. With a
   * requested target date, a photo more than 12 hours away is flagged as the
   * nearest available match.
   */
  const toImage = useCallback(
    (
      imageId: string | undefined,
      images: ImageLookup,
      fallbackIso: string,
      targetMs?: number,
    ): ComparisonImage => {
      const info = imageId ? images[imageId] : undefined;
      const date = formatDate(info?.date ?? fallbackIso);
      const image: ComparisonImage = {
        src: info?.url ?? '',
        alt: t('comparison.image.dateLabel', { date }),
        date,
      };
      if (info && targetMs !== undefined && isNearestMatch(info.date, targetMs)) {
        image.isNearestMatch = true;
      }
      return image;
    },
    [formatDate, t],
  );

  const toResult = useCallback(
    (
      { record, images }: WithImages<ComparisonDetail>,
      targets?: ComparisonTargetDates | null,
    ): ComparisonResult => ({
      id: record.id,
      changeCategory: record.result.changeCategory,
      comparability: record.comparability,
      confidence: record.result.confidence,
      description: record.result.description,
      sourceImageIds: record.result.sourceImageIds,
      // imageIds are persisted earlier photo first (see resolveImages on the backend).
      beforeImage: toImage(record.imageIds[0], images, record.createdAt, targets?.before),
      afterImage: toImage(
        record.imageIds[record.imageIds.length - 1],
        images,
        record.createdAt,
        targets?.after,
      ),
      // Explicitly chosen comparisons may hold up to five photos: the ones between
      // the earliest and the latest are shown too (never nearest-match flagged).
      intermediateImages: record.imageIds
        .slice(1, -1)
        .map(imageId => toImage(imageId, images, record.createdAt)),
    }),
    [toImage],
  );

  const toHistoryItem = useCallback(
    (record: ComparisonDetail, images: ImageLookup): ComparisonHistoryItem => ({
      id: record.id,
      fromDate: toImage(record.imageIds[0], images, record.createdAt).date,
      toDate: toImage(record.imageIds[record.imageIds.length - 1], images, record.createdAt).date,
      changeCategory: record.result.changeCategory,
      description: record.result.description,
      comparability: record.comparability,
      triggeredBy: record.triggeredBy,
    }),
    [toImage],
  );

  return { toResult, toHistoryItem, formatDate };
}

/**
 * Fetches the comparison history (newest first) for an aquarium, cursor-paginated
 * (GET /comparisons returns `{ data, nextCursor }`). `data` is every loaded page
 * flattened and mapped onto ComparisonView history items; `fetchNextPage` loads
 * the next (older) page while `hasNextPage` is true. Disabled when aquariumId is empty.
 */
export function useComparisonHistory(
  aquariumId: string | undefined,
): UseInfiniteQueryResult<ComparisonHistoryItem[], Error> {
  const queryClient = useQueryClient();
  const { toHistoryItem } = useComparisonFormatters();

  const select = useCallback(
    (data: InfiniteData<HistoryPage, string | null>) =>
      data.pages.flatMap(page => page.record.map(item => toHistoryItem(item, page.images))),
    [toHistoryItem],
  );

  return useInfiniteQuery({
    queryKey: comparisonQueryKeys.history(aquariumId ?? ''),
    queryFn: async ({ pageParam }): Promise<HistoryPage> => {
      const id = aquariumId as string;
      const { items, nextCursor } = await listComparisons(id, pageParam);
      const imageIds = [...new Set(items.flatMap(item => item.imageIds))];
      return { record: items, images: await loadImages(queryClient, id, imageIds), nextCursor };
    },
    initialPageParam: null as string | null,
    getNextPageParam: lastPage => lastPage.nextCursor ?? undefined,
    select,
    enabled: Boolean(aquariumId),
    staleTime: STALE_TIME_MS,
  });
}

/**
 * Fetches a single comparison for read-only reopening from history (never re-runs the AI call).
 * Enabled only when both aquariumId and comparisonId are present.
 */
export function useComparisonDetail(
  aquariumId: string | undefined,
  comparisonId: string | null,
): UseQueryResult<ComparisonResult, Error> {
  const queryClient = useQueryClient();
  const { toResult } = useComparisonFormatters();

  // A reopened comparison has no requested target dates, so isNearestMatch is never set.
  const select = useCallback((data: WithImages<ComparisonDetail>) => toResult(data), [toResult]);

  return useQuery({
    queryKey: comparisonQueryKeys.detail(aquariumId ?? '', comparisonId ?? ''),
    queryFn: async (): Promise<WithImages<ComparisonDetail>> => {
      const id = aquariumId as string;
      const record = await getComparison(id, comparisonId as string);
      return { record, images: await loadImages(queryClient, id, record.imageIds) };
    },
    select,
    enabled: Boolean(aquariumId) && Boolean(comparisonId),
    staleTime: STALE_TIME_MS,
  });
}

/**
 * Triggers a new comparison run (POST /comparisons, synchronous) and resolves
 * with the mapped result. Each image is flagged `isNearestMatch` when its photo
 * date is more than 12 hours from the requested date (preset: today / today − N
 * days; custom: dateFrom / dateTo). On success, invalidates the history query
 * for this aquarium so the new comparison appears in the history list.
 */
export function useCreateComparison(
  aquariumId: string | undefined,
): UseMutationResult<ComparisonResult, Error, CreateComparisonPayload> {
  const queryClient = useQueryClient();
  const { toResult } = useComparisonFormatters();

  return useMutation({
    mutationFn: async (payload: CreateComparisonPayload) => {
      const id = aquariumId as string;
      const targets = getComparisonTargetDates(payload);
      const record = await createComparison(id, payload);
      return toResult(
        { record, images: await loadImages(queryClient, id, record.imageIds) },
        targets,
      );
    },
    onSuccess: () => {
      if (aquariumId) {
        void queryClient.invalidateQueries({ queryKey: comparisonQueryKeys.history(aquariumId) });
      }
    },
    // A comparison is an AI call: keep the credit meter current.
    onSettled: () => {
      invalidateAiUsage(queryClient);
    },
  });
}

/**
 * Deletes one comparison (DELETE /comparisons/:id). On success the history is
 * refetched and the cached detail dropped, so a reopened view cannot show it again.
 */
export function useDeleteComparison(aquariumId: string | undefined): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (comparisonId: string) => deleteComparison(aquariumId as string, comparisonId),
    onSuccess: (_data, comparisonId) => {
      if (!aquariumId) return;
      queryClient.removeQueries({ queryKey: comparisonQueryKeys.detail(aquariumId, comparisonId) });
      void queryClient.invalidateQueries({ queryKey: comparisonQueryKeys.history(aquariumId) });
    },
  });
}

/**
 * Deletes every comparison of the aquarium (DELETE /comparisons); resolves with
 * the number deleted. On success the history is refetched and all details dropped.
 */
export function useDeleteAllComparisons(aquariumId: string | undefined): UseMutationResult<number, Error, void> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => deleteAllComparisons(aquariumId as string),
    onSuccess: () => {
      if (!aquariumId) return;
      queryClient.removeQueries({ queryKey: [...comparisonQueryKeys.all(aquariumId), 'detail'] });
      void queryClient.invalidateQueries({ queryKey: comparisonQueryKeys.history(aquariumId) });
    },
  });
}

/**
 * Pages through the aquarium gallery (GET /api/v1/aquariums/:id/images) for the
 * "choose photos" picker. `data` is every loaded photo, newest photo first by
 * photo date, with its locale-formatted date; `fetchNextPage` loads more while
 * `hasNextPage` is true. Only enabled while the picker is shown.
 */
export function useComparisonGallery(
  aquariumId: string | undefined,
  enabled: boolean,
): UseInfiniteQueryResult<ComparisonPhotoOption[], Error> {
  const { formatDate } = useComparisonFormatters();

  const select = useCallback(
    (data: InfiniteData<ComparisonGalleryPage, number>): ComparisonPhotoOption[] => {
      const seen = new Set<string>();
      const options: ComparisonPhotoOption[] = [];
      for (const page of data.pages) {
        for (const photo of page.photos) {
          if (seen.has(photo.id)) continue;
          seen.add(photo.id);
          options.push({
            id: photo.id,
            takenAt: photo.takenAt,
            src: photo.thumbnailUrl ?? '',
            date: formatDate(photo.takenAt),
          });
        }
      }
      const time = (iso: string) => {
        const ms = Date.parse(iso);
        return Number.isNaN(ms) ? Number.NEGATIVE_INFINITY : ms;
      };
      return options.sort((a, b) => time(b.takenAt) - time(a.takenAt) || (a.id < b.id ? -1 : 1));
    },
    [formatDate],
  );

  return useInfiniteQuery({
    queryKey: comparisonQueryKeys.gallery(aquariumId ?? ''),
    queryFn: ({ pageParam }) =>
      fetchComparisonGalleryPage(aquariumId as string, pageParam, COMPARISON_GALLERY_PAGE_SIZE),
    initialPageParam: 1,
    getNextPageParam: lastPage => (lastPage.hasMore ? lastPage.page + 1 : undefined),
    select,
    enabled: Boolean(aquariumId) && enabled,
    staleTime: STALE_TIME_MS,
  });
}
